import { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../../../utils/api/ApiUtils';

export const useERDState = (username, apiKey, refreshUserStats, getClassContext, documentId = null, source = 'main') => {
  // Track class context changes
  const [classContext, setClassContext] = useState(null);

  // Update class context when it changes
  useEffect(() => {
    const currentClassContext = getClassContext ? getClassContext() : null;
    setClassContext(currentClassContext);
  }, [getClassContext]);

  const [inputText, setInputText] = useState('');
  const [relationships, setRelationships] = useState([]);
  const [selectedRelationship, setSelectedRelationship] = useState('All');
  const [erdData, setErdData] = useState(null);
  const [svgContent, setSvgContent] = useState(null);

  // Feedback state — unified items array (text, qa, mcq, short_answer)
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [scaffoldStages, setScaffoldStages] = useState([]); // scaffold stages from AI guideline
  const [stage0Type, setStage0Type] = useState('mcq'); // type of the first scaffold stage
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [feedbackId, setFeedbackId] = useState(null);
  const [hasErrors, setHasErrors] = useState(true);
  const [errorTypes, setErrorTypes] = useState([]);
  const [relevantStatements, setRelevantStatements] = useState([]);

  // Environment and user state
  const [selectedEnvironment, setSelectedEnvironment] = useState('None');
  const [environments, setEnvironments] = useState(['None']);
  const [userStats, setUserStats] = useState({ feedbackCount: 0, quotaValue: 20 });
  const [workingEnvironment, setWorkingEnvironment] = useState('default');
  const [hasRequirements, setHasRequirements] = useState(false);

  // UI state
  const [leftPanelWidth, setLeftPanelWidth] = useState(60);
  const [textareaHeight, setTextareaHeight] = useState(256);
  const [editorMode, setEditorMode] = useState('raw');
  const [showRestoreNotification, setShowRestoreNotification] = useState(false);

  // Loading states
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGettingFeedback, setIsGettingFeedback] = useState(false);

  // Visual editor state
  const [newEntity, setNewEntity] = useState({
    name: '',
    attributes: [''],
    entityTypes: []
  });
  const [newRelationship, setNewRelationship] = useState({
    name: '',
    participants: [{ entity: '', cardinality: '1', participationTypes: [] }],
    attributes: [''],
    relationshipTypes: []
  });
  const [availableEntities, setAvailableEntities] = useState([]);

  // Sample ERD notation
  const sampleERD = `[Entity]
Employee
--
FirstName
LastName
SSN @key
Address (street,city,state)
Phone @multivalued

[Entity]
SalaryEmployee
--
Salary

[Entity]
HourlyEmployee
--
Hour_rate

[Entity]
Department
--
Name
Location @multivalued
Number @key

[Entity]
Dependent @weak
--
name (firstname,lastname) @partial_key

[Relationship]
works_for
--
Employee (1) 
Department (N)

[Relationship]
Has @identifying
--
Employee (1)
Dependent (N) @total_participation

[Specialization]
Employee @disjoint
--
HourlyEmployee
SalaryEmployee`;

  // Activity tracking helper (only for embedded ERD in documents)
  const logERDActivity = useCallback(async (activityType, description, metadata = {}) => {
    // Debug logging
    console.log('[useERDState] logERDActivity called:', {
      activityType,
      description,
      documentId,
      hasClassContext: !!classContext,
      source,
      metadata
    });

    // Only log if we have documentId (means it's embedded) and classContext
    if (!documentId || !classContext) {
      console.warn('[useERDState] Activity logging skipped - missing documentId or classContext:', {
        documentId,
        hasClassContext: !!classContext
      });
      return;
    }

    try {
      const { activityApi } = await import('../../../utils/api/DocumentEditorApi/ActivityApi');
      console.log('[useERDState] Calling activityApi.logActivity with:', {
        documentId,
        activityType,
        description,
        classContext
      });

      await activityApi.logActivity(
        documentId,
        activityType,
        description,
        {
          metadata: {
            source: source,  // 'embedded' or 'main'
            erdNotationType: 'Chen',  // Notation type (Chen, Crow's Foot, UML)
            erdNotationText: inputText?.substring(0, 500),  // First 500 chars of notation for reference
            ...metadata
          }
        },
        classContext
      );
      console.log('[useERDState] Activity logged successfully:', activityType);
    } catch (err) {
      console.error(`[useERDState] Failed to log ${activityType} activity:`, err);
    }
  }, [documentId, classContext, source, inputText]);

  // Parse entities from current notation
  const parseEntitiesFromNotation = useCallback(() => {
    const entityMatches = inputText.match(/\[Entity\]\s*\n\s*(\w+)/g);
    if (entityMatches) {
      const entities = entityMatches.map(match => {
        const name = match.replace(/\[Entity\]\s*\n\s*/, '');
        return name;
      });
      setAvailableEntities(entities);
    } else {
      setAvailableEntities([]);
    }
  }, [inputText]);

  // Track when user switches environments to update working context
  useEffect(() => {
    if (selectedEnvironment && selectedEnvironment !== 'None') {
      setWorkingEnvironment(selectedEnvironment);
    }
  }, [selectedEnvironment]);

  // Check whether the selected environment has requirements configured
  useEffect(() => {
    if (!selectedEnvironment || selectedEnvironment === 'None') {
      setHasRequirements(false);
      return;
    }
    const classContext = getClassContext ? getClassContext() : null;
    if (!classContext?.class_code) {
      setHasRequirements(false);
      return;
    }
    fetch(`/api/requirements/${selectedEnvironment}`, {
      headers: { 'Content-Type': 'application/json', 'Class-Code': classContext.class_code },
      credentials: 'include'
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => setHasRequirements(!!(data?.requirements?.length)))
      .catch(() => setHasRequirements(false));
  }, [selectedEnvironment, getClassContext]);

  // Update available entities when notation changes
  useEffect(() => {
    parseEntitiesFromNotation();
  }, [inputText, parseEntitiesFromNotation]);

  // Enhanced auto-save functionality
  // IMPORTANT: Only auto-save for the main ERD editor (source === 'main')
  // For embedded ERD in documents (source === 'embedded'), don't auto-save to localStorage
  // because ERD content is saved with the document
  useEffect(() => {
    // Skip auto-save for embedded ERD
    if (source === 'embedded') {
      return;
    }

    const saveTimer = setTimeout(() => {
      const workState = {
        inputText,
        selectedRelationship,
        selectedEnvironment,
        erdData,
        svgContent,
        relationships,
        feedbackItems,
        rating,
        comment,
        feedbackId,
        textareaHeight,
        leftPanelWidth,
        editorMode
      };

      if (username) {
        localStorage.setItem(`erd_work_state_${username}`, JSON.stringify(workState));
      }
    }, 1000);

    return () => clearTimeout(saveTimer);
  }, [
    inputText, selectedRelationship, selectedEnvironment, erdData,
    svgContent, relationships, feedbackItems, rating, comment,
    feedbackId, textareaHeight, leftPanelWidth, username, editorMode, source
  ]);

  // Load complete work state on component mount
  // IMPORTANT: Only restore state for the main ERD editor (source === 'main')
  // For embedded ERD in documents (source === 'embedded'), don't restore localStorage state
  // because ERD content should come from the document itself (notation comes from markdown, not localStorage)
  // Specifically, we don't want to restore svgContent and erdData from localStorage for embedded ERD
  useEffect(() => {
    if (!username) return;

    // Skip localStorage restore for embedded ERD - document content will be loaded by ERDSnippet
    if (source === 'embedded') {
      console.log('[useERDState] Skipping localStorage restore for embedded ERD in document');
      return;
    }

    try {
      const savedWorkState = localStorage.getItem(`erd_work_state_${username}`);
      if (savedWorkState) {
        const workState = JSON.parse(savedWorkState);

        setInputText(workState.inputText || '');
        setSelectedRelationship(workState.selectedRelationship || 'All');
        setSelectedEnvironment(workState.selectedEnvironment || 'Hospital');
        setErdData(workState.erdData || null);
        setSvgContent(workState.svgContent || null);
        setRelationships(workState.relationships || []);
        // Restore feedbackItems with backward compatibility for old format
        if (workState.feedbackItems) {
          setFeedbackItems(workState.feedbackItems.filter(i => i.type !== 'mcq' && i.type !== 'short_answer'));
        } else {
          // Transform old format (separate feedback/qa arrays) to unified items
          const items = [];
          (workState.feedback || []).forEach(text => {
            if (text && text.trim()) items.push({ type: 'text', content: text });
          });
          (workState.qa || []).forEach(qa => {
            items.push({ type: 'qa', question: qa.question, answer: qa.answer });
          });
          setFeedbackItems(items);
        }
        setRating(workState.rating || 5);
        setComment(workState.comment || '');
        setFeedbackId(workState.feedbackId || null);
        setTextareaHeight(workState.textareaHeight || 256);
        setLeftPanelWidth(workState.leftPanelWidth || 60);
        setEditorMode(workState.editorMode || 'raw');

        const restoredItems = workState.feedbackItems || workState.feedback || [];
        if (workState.inputText || workState.svgContent || restoredItems.length > 0) {
          setShowRestoreNotification(true);
          setTimeout(() => setShowRestoreNotification(false), 5000);
        }
      }
    } catch (error) {
      console.error('Error loading saved work state:', error);
    }
  }, [username, source]);

  // Fetch user stats and environments
  useEffect(() => {
    const fetchUserStats = async () => {
      try {
        // Build URL with optional class_id parameter for class-scoped stats
        let url = `/api/user-stats/${username}`;

        // Add class_id if we have class context
        if (classContext && classContext.class_id) {
          url += `?class_id=${encodeURIComponent(classContext.class_id)}`;
        }

        const response = await fetch(url, {
          credentials: 'include',
          headers: getAuthHeaders(classContext)
        });
        if (response.ok) {
          const stats = await response.json();
          setUserStats(stats);
        }
      } catch (error) {
        console.error('Error fetching user stats:', error);
      }
    };

    if (username) {
      fetchUserStats();
    }
  }, [username, classContext]);


  useEffect(() => {
    const fetchEnvironments = async () => {
      try {
        let url = '/api/environments';
        
        // Add class filter if we have a specific class selected
        if (classContext && classContext.class_code) {
          url += `?class_code=${encodeURIComponent(classContext.class_code)}`;
        } else {
          // If no class is selected, reset environment to 'None'
          setSelectedEnvironment('None');
        }
        
        const response = await fetch(url, {
          credentials: 'include',
          headers: getAuthHeaders(classContext)
        });
        if (response.ok) {
          const data = await response.json();
          const uniqueEnvironments = [...new Set(data.environments)];
          const sortedEnvironments = uniqueEnvironments.sort((a, b) => {
            if (a === 'None') return -1;
            if (b === 'None') return 1;
            return a.localeCompare(b);
          });
          setEnvironments(sortedEnvironments);

          // If no class is selected, make sure environment is set to 'None'
          if (!classContext || !classContext.class_code) {
            setSelectedEnvironment('None');
          }

          // Cache successful result in localStorage with class-specific key
          const cacheKey = classContext ? `cachedEnvironments_${classContext.class_code}` : 'cachedEnvironments';
          localStorage.setItem(cacheKey, JSON.stringify(sortedEnvironments));
        }
      } catch (error) {
        console.error('Error fetching environments:', error);

        // Try to load from localStorage cache
        try {
          const cacheKey = classContext ? `cachedEnvironments_${classContext.class_code}` : 'cachedEnvironments';
          const cached = localStorage.getItem(cacheKey);
          if (cached) {
            const cachedEnvironments = JSON.parse(cached);
            setEnvironments(cachedEnvironments);
            console.log('Loaded environments from cache:', cachedEnvironments);
          } else {
            // Only use hardcoded fallback if no cache exists
            setEnvironments(['None']);
          }
          
          // If no class is selected, make sure environment is set to 'None'
          if (!classContext || !classContext.class_code) {
            setSelectedEnvironment('None');
          }
        } catch (cacheError) {
          console.error('Error loading cached environments:', cacheError);
          setEnvironments(['None']);
          setSelectedEnvironment('None');
        }
      }
    };

    fetchEnvironments();
  }, [classContext]);


  // Reset function
  const handleReset = useCallback(() => {
    setInputText('');
    setRelationships([]);
    setSelectedRelationship('All');
    setErdData(null);
    setSvgContent(null);
    setFeedbackItems([]);
    setRating(5);
    setComment('');
    setFeedbackId(null);
    setTextareaHeight(256);
    setNewEntity({ name: '', attributes: [''], entityTypes: [] });
    setNewRelationship({
      name: '',
      participants: [{ entity: '', cardinality: '1', participationTypes: [] }],
      attributes: [''],
      relationshipTypes: []
    });

    if (username) {
      localStorage.removeItem(`erd_work_state_${username}`);
      localStorage.removeItem(`erd_notation_${username}`);
      localStorage.removeItem(`textarea_height_${username}`);
      localStorage.removeItem(`panel_width_${username}`);
    }
  }, [username]);

  // Load sample ERD
  const loadSampleERD = () => {
    setInputText(sampleERD);
  };

  // Download notation
  const downloadNotation = () => {
    const element = document.createElement('a');
    const file = new Blob([inputText], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'erd_notation.txt';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // Generate ERD - Monitor actual state values for save timing
  const generateERD = useCallback(async (onGenerateComplete = null, customNotation = null) => {
    const notationToUse = customNotation || inputText;

    if (!notationToUse.trim()) {
      alert('Please enter ERD notation');
      return;
    }

    setIsGenerating(true);

    // Log ERD generation activity
    await logERDActivity(
      'erd_generated',
      'Generated ERD diagram',
      {
        relationship: selectedRelationship,
        environment: selectedEnvironment,
        notationLength: notationToUse.length,
        entityCount: availableEntities?.length || 0
      }
    );

    try {
      // Only include username if it's a valid username (not default 'student')
      // Backend will use current_user.username if username is not provided
      const requestBody = {
        notation: notationToUse,
        relationship: selectedRelationship
      };

      // Only add username if it's provided and not the default placeholder
      if (username && username !== 'student') {
        requestBody.username = username;
      }

      const response = await fetch('/api/generate-erd', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const data = await response.json();

        // Debug logging
        console.log('API response data:', {
          hasFilteredSvg: !!data.svgBase64,
          hasFullSvg: !!data.fullSvgBase64,
          relationship: selectedRelationship
        });

        // Prepare the values we're going to set
        const erdDataValue = data.erdData;
        const originalErdDataValue = data.originalErdData || data.erdData;
        const svgContentValue = data.svgBase64
          ? `<img src="data:image/svg+xml;base64,${data.svgBase64}" alt="ERD Diagram" style="max-width: 100%; height: auto; display: block; margin: 0 auto;" />`
          : null;
        const fullSvgContentValue = data.fullSvgBase64
          ? `<img src="data:image/svg+xml;base64,${data.fullSvgBase64}" alt="ERD Diagram" style="max-width: 100%; height: auto; display: block; margin: 0 auto;" />`
          : svgContentValue;
        const relationshipsValue = data.relationships || [];

        // Set the state - use original data for complete ERD storage
        setErdData(originalErdDataValue);
        setSvgContent(svgContentValue);
        setRelationships(relationshipsValue);

        // Only trigger save if we have actual data to save
        if (onGenerateComplete && fullSvgContentValue && originalErdDataValue) {
          console.log('Triggering save after ERD generation with data:', {
            svgContent: fullSvgContentValue ? 'present' : 'null',
            erdData: originalErdDataValue ? 'present' : 'null',
            relationships: relationshipsValue.length
          });
          
          // Pass the actual values directly to avoid state timing issues
          onGenerateComplete('erd_generated', {
            erdData: erdDataValue,
            originalErdData: originalErdDataValue,
            svgContent: fullSvgContentValue,
            fullSvgContent: fullSvgContentValue,
            relationships: relationshipsValue
          });
        } else if (onGenerateComplete && (!fullSvgContentValue || !originalErdDataValue)) {
          console.warn('Not saving - missing required data:', {
            svgContent: fullSvgContentValue ? 'present' : 'missing',
            erdData: originalErdDataValue ? 'present' : 'missing'
          });
        }
      } else {
        // DEBUG ERROR RESPONSE LOGGING
        const errorText = await response.text();

        let errorData = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          // If it's not JSON, use the raw text
          errorData = { error: errorText };
        }

        const errorMessage = errorData.error || 'Failed to generate ERD';

        // Log failed ERD generation attempt
        await logERDActivity(
          'erd_generation_failed',
          `Failed to generate ERD: ${errorMessage}`,
          {
            relationship: selectedRelationship,
            environment: selectedEnvironment,
            notationLength: notationToUse.length,
            entityCount: availableEntities?.length || 0,
            errorMessage: errorMessage,
            httpStatus: response.status
          }
        );

        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error generating ERD:', error);

      // Log failed ERD generation (if not already logged above)
      if (!error.message.includes('Failed to generate ERD')) {
        await logERDActivity(
          'erd_generation_failed',
          `ERD generation error: ${error.message}`,
          {
            relationship: selectedRelationship,
            environment: selectedEnvironment,
            notationLength: notationToUse.length,
            entityCount: availableEntities?.length || 0,
            errorMessage: error.message
          }
        );
      }

      alert('Error generating ERD: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  }, [inputText, selectedRelationship, username, selectedEnvironment, availableEntities, logERDActivity]);


  // Parse notation for visual editor recreation
  const parseNotationForVisual = useCallback((notation) => {
    if (!notation) return;

    try {
      // Update the input text first
      setInputText(notation);

      // Parse entities for the visual editor
      const entityMatches = notation.match(/\[Entity\]\s*\n\s*(\w+)/g);
      if (entityMatches) {
        const entities = entityMatches.map(match => {
          const name = match.replace(/\[Entity\]\s*\n\s*/, '');
          return name;
        });
        setAvailableEntities(entities);
      }

      // Generate ERD if the notation is valid
      setTimeout(() => {
        if (notation.trim()) {
          generateERD(null, notation); // Pass the notation directly
        }
      }, 200);
    } catch (error) {
      console.error('Error parsing notation for visual editor:', error);
    }
  }, [generateERD]);

  // Handle version loading
  const handleVersionLoad = useCallback((workData) => {
    try {
      // 1. Determine actual environment
      const actualEnvironment = workData.actualEnvironment ||
        workData.metadata?.environment ||
        workData.erdState?.selectedEnvironment ||
        'default';

      // 2. Update working environment state
      setWorkingEnvironment(actualEnvironment);

      // 3. Sync environment dropdown
      setSelectedEnvironment(actualEnvironment);

      // 4. Update text area with reconstructed raw notation
      if (workData.erdState?.inputText) {
        setInputText(workData.erdState.inputText);
      }

      // 5. Update visual editor with ERD data
      if (workData.erdState?.erdData) {
        setErdData(workData.erdState.erdData);
      }

      // 6. Update other states as needed
      if (workData.erdState?.selectedRelationship) {
        setSelectedRelationship(workData.erdState.selectedRelationship);
      }

      // 7. Update SVG content
      if (workData.erdState?.svgContent) {
        setSvgContent(workData.erdState.svgContent);
      }

      // 8. Update feedback data (with backward compatibility)
      if (workData.erdState?.feedbackItems) {
        setFeedbackItems(workData.erdState.feedbackItems.filter(i => i.type !== 'mcq' && i.type !== 'short_answer'));
      } else {
        // Transform old format to unified items
        const items = [];
        (workData.erdState?.feedback || []).forEach(text => {
          if (text && text.trim()) items.push({ type: 'text', content: text });
        });
        (workData.erdState?.qa || []).forEach(qa => {
          items.push({ type: 'qa', question: qa.question, answer: qa.answer });
        });
        if (items.length > 0) setFeedbackItems(items);
      }
      
      // 9. Update UI state
      if (workData.erdState?.textareaHeight) {
        setTextareaHeight(workData.erdState.textareaHeight);
      }
      if (workData.erdState?.leftPanelWidth) {
        setLeftPanelWidth(workData.erdState.leftPanelWidth);
      }
      if (workData.erdState?.rating) {
        setRating(workData.erdState.rating);
      }
      if (workData.erdState?.comment) {
        setComment(workData.erdState.comment);
      }
      if (workData.erdState?.feedbackId) {
        setFeedbackId(workData.erdState.feedbackId);
      }
      if (workData.erdState?.relationships) {
        setRelationships(workData.erdState.relationships);
      }

      // 10. Show restore notification
      setShowRestoreNotification(true);
      setTimeout(() => setShowRestoreNotification(false), 5000);

      // 11. If visual editor needs updating and there's notation, recreate visual components
      if (workData.erdState?.inputText) {
        setTimeout(() => {
          if (parseNotationForVisual) {
            parseNotationForVisual(workData.erdState.inputText);
          }
        }, 100);
      }

      console.log('Version loaded successfully for environment:', actualEnvironment);

    } catch (error) {
      console.error('Error loading version:', error);
      // You could set an error state here if you have one
    }
  }, [parseNotationForVisual]);

  // Get feedback
  const getFeedback = async (envOverride, requirementsOverride, sectionId) => {
    const environment = envOverride || selectedEnvironment;

    if (!apiKey) {
      alert('You need OpenAI API Key to receive feedback');
      return;
    }

    if (!environment || environment === 'None') {
      alert('Please select an environment before requesting feedback.');
      return;
    }

    if (!hasRequirements && !envOverride) {
      alert('No requirements are configured for this environment. Please contact your instructor.');
      return;
    }

    if (selectedRelationship === 'All') {
      console.warn('[useERDState] Cannot generate feedback - selectedRelationship is "All"');
      alert('To get feedback, select a relationship and re-draw the ERD for the selected relationship');
      return;
    }

    // Quota enforcement is handled server-side — do not gate here with stale client state

    setIsGettingFeedback(true);

    // Log feedback request activity
    await logERDActivity(
      'erd_feedback_requested',
      'Requested AI feedback for ERD',
      {
        relationship: selectedRelationship,
        environment,
        entityCount: availableEntities?.length || 0,
        relationshipCount: relationships?.length || 0
      }
    );

    try {
      const response = await fetch('/api/get-feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username,
          environment,
          relationship: selectedRelationship,
          erdData: erdData,
          notation: inputText,
          class_id: classContext?.class_id, // Include class_id for quota tracking
          requirements: requirementsOverride || undefined, // inline requirements from document block
          section_id: sectionId || undefined // section ID so backend can fetch the AI guideline
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setFeedbackItems(data.items || []);
        setScaffoldStages(data.scaffoldStages || []);
        setStage0Type(data.stage0Type || 'mcq');
        setFeedbackId(data.feedbackId);
        setHasErrors(data.hasErrors !== undefined ? data.hasErrors : true);
        setErrorTypes(data.errorTypes || []);
        setRelevantStatements(data.relevantStatements || []);

        // Update local feedback count immediately
        setUserStats(prev => ({
          ...prev,
          feedbackCount: prev.feedbackCount + 1
        }));

        // IMMEDIATE: Trigger all UI updates
        if (refreshUserStats) {
          refreshUserStats();
        }

        // Log feedback viewed activity
        await logERDActivity(
          'erd_feedback_viewed',
          'Viewed AI feedback for ERD',
          {
            feedbackId: data.feedbackId,
            relationship: selectedRelationship,
            itemCount: data.items?.length || 0
          }
        );
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get feedback');
      }
    } catch (error) {
      console.error('Error getting feedback:', error);
      alert('Error getting feedback: ' + error.message);
    } finally {
      setIsGettingFeedback(false);
    }
  };

  // Submit rating
  const submitRating = async () => {
    if (!feedbackId) {
      alert('Error in submitting the rating!');
      return;
    }

    try {
      const response = await fetch('/api/submit-rating', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          feedbackId: feedbackId,
          rating: rating,
          comment: comment
        }),
      });

      if (response.ok) {
        alert('Thank you for rating this feedback!');

        // Log rating submission activity
        await logERDActivity(
          'erd_feedback_rated',
          'Rated AI feedback for ERD',
          {
            feedbackId: feedbackId,
            rating: rating,
            hasComment: comment.length > 0,
            commentLength: comment.length
          }
        );

        setComment('');
        setRating(5);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to submit rating');
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      alert('Error submitting rating: ' + error.message);
    }
  };

  // Submit answer to a pedagogical agent MCQ question
  const submitQuestionAnswer = async (questionId, selectedOptions, questionData = {}) => {
    try {
      const classContext = getClassContext ? getClassContext() : null;
      const classCode = classContext?.class_code;
      if (!classCode || !selectedEnvironment) {
        return { success: false, error: 'Missing class or environment context' };
      }

      const { submitAnswer } = await import('../../../utils/api/ERDQuestionsApi');
      const result = await submitAnswer(
        selectedEnvironment,
        questionId,
        selectedOptions,
        feedbackId,
        classContext,
        questionData
      );
      // Log activity
      await logERDActivity(
        'erd_question_answered',
        'Answered pedagogical agent question',
        {
          questionId,
          selectedOptions,
          isCorrect: result.is_correct,
          feedbackId
        }
      );

      return result;
    } catch (error) {
      console.error('Error submitting question answer:', error);
      return { success: false, error: error.message };
    }
  };

  // Submit short answer response (stored for instructor review)
  const submitShortAnswer = async (questionId, answerText, questionText = '') => {
    try {
      const classContext = getClassContext ? getClassContext() : null;
      const classCode = classContext?.class_code;
      if (!classCode || !selectedEnvironment) {
        return { success: false, error: 'Missing class or environment context' };
      }

      const { submitShortAnswer: submitShortAnswerApi } = await import('../../../utils/api/ERDQuestionsApi');
      const result = await submitShortAnswerApi(
        selectedEnvironment,
        questionId,
        answerText,
        feedbackId,
        classContext,
        questionText
      );

      await logERDActivity(
        'erd_short_answer_submitted',
        'Submitted short answer response',
        {
          questionId,
          feedbackId
        }
      );

      return result;
    } catch (error) {
      console.error('Error submitting short answer:', error);
      return { success: false, error: error.message };
    }
  };

  // Log FAQ click
  const logFAQClick = async (questionIndex, question) => {
    await logERDActivity(
      'erd_faq_opened',
      'Opened FAQ question in ERD feedback',
      {
        questionIndex: questionIndex,
        question: question.substring(0, 100), // First 100 chars
        feedbackId: feedbackId
      }
    );
  };

  return {
    // State
    inputText,
    setInputText,
    relationships,
    setRelationships,
    selectedRelationship,
    setSelectedRelationship,
    erdData,
    setErdData,
    svgContent,
    setSvgContent,
    feedbackItems,
    setFeedbackItems,
    scaffoldStages,
    setScaffoldStages,
    stage0Type,
    setStage0Type,
    rating,
    setRating,
    comment,
    setComment,
    feedbackId,
    setFeedbackId,
    hasErrors,
    setHasErrors,
    errorTypes,
    setErrorTypes,
    relevantStatements,
    setRelevantStatements,
    selectedEnvironment,
    setSelectedEnvironment,
    workingEnvironment,
    setWorkingEnvironment,
    environments,
    setEnvironments,
    userStats,
    setUserStats,
    leftPanelWidth,
    setLeftPanelWidth,
    textareaHeight,
    setTextareaHeight,
    editorMode,
    setEditorMode,
    showRestoreNotification,
    setShowRestoreNotification,
    isGenerating,
    setIsGenerating,
    isGettingFeedback,
    setIsGettingFeedback,
    newEntity,
    setNewEntity,
    newRelationship,
    setNewRelationship,
    availableEntities,
    setAvailableEntities,
    hasRequirements,

    // Functions
    handleReset,
    loadSampleERD,
    downloadNotation,
    generateERD,
    getFeedback,
    submitRating,
    submitQuestionAnswer,
    submitShortAnswer,
    parseEntitiesFromNotation,
    parseNotationForVisual,
    handleVersionLoad,
    sampleERD,
    logFAQClick  // Add FAQ tracking function
  };
};